import React from 'react';
import { Avatar } from '../Avatar/Avatar';
import { deleteComment } from '../../utils/apiComments';


interface CommentProps {
  commentId: string;
  projectId: any;
  patientId: any;
  uiNotificationService?: any;
  userAlias?: string;
  timestamp?: Date;
  text?: string;
  tags: string[];
  isCurrentUserComment?: boolean;
  onCommentDeleted?: (_commentId: string) => void;
}

const Comment: React.FC<CommentProps> = ({
  commentId,
  projectId,
  patientId,
  uiNotificationService,
  userAlias = 'anonymous',
  timestamp = new Date(),
  text = 'No comment provided',
  tags,
  isCurrentUserComment = false,
  onCommentDeleted,
}) => {
  const formattedDate = timestamp.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const formattedTime = timestamp.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleDelete = async () => {
    try {
      const response = await deleteComment(projectId, patientId, 'CT', commentId);

      if (response?.error === 'UNAUTHORIZED') {
        uiNotificationService?.show({
          title: 'Comments',
          message: 'Unauthorized while deleting comment.',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      onCommentDeleted?.(commentId);
      uiNotificationService?.show({
        title: 'Comments',
        message: 'Comment deleted successfully.',
        type: 'success',
        duration: 2500,
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      uiNotificationService?.show({
        title: 'Comments',
        message: 'Could not delete comment.',
        type: 'error',
        duration: 3000,
      });
    }
  };
  return (
    <div
      className={`mt-4 mb-2 flex items-start gap-2 ${
        isCurrentUserComment ? 'flex-row-reverse' : 'flex-row'
      }`}
    >
      <Avatar
        size={30}
        email={userAlias}
      />

      <div
        key={commentId}
        style={{
          backgroundColor: isCurrentUserComment ? '#e8f5e9' : '#e2e2e2',
        }}
        className={`relative max-w-[90%] flex-1 p-3 text-sm text-black ${
          isCurrentUserComment ? 'rounded-l-xl rounded-br-xl' : 'rounded-r-xl rounded-bl-xl'
        }`}
      >
        <div className="mb-2 flex items-center justify-between gap-4">
          <span className="truncate font-bold text-black">{userAlias}</span>

          <span className="shrink-0 text-xs text-black">
            {formattedDate} · {formattedTime}
          </span>
        </div>

        <p className="mb-4 whitespace-pre-wrap break-words text-black">{text}</p>

        <div className="mt-2 flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={`${tag}-${index}`}
              className="rounded-full border border-[#b4b4b4]  bg-white/30 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              {tag}
            </span>
          ))}
        </div>

        <button
          type="button"
          aria-label="delete comment"
          onClick={handleDelete}
          className="absolute bottom-2 right-2 rounded-full p-1 transition-colors hover:bg-red-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#d32f2f"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Comment;
